<template>
  <v-dialog
    :value="value"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    persistent
    scrollable
    max-width="980"
    @input="v => !v && close()"
  >
    <v-card class="stickers">
      <div class="fdlg__form">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">Imprimă etichete</h2>
            <p class="fdlg__sub">{{ countText }}</p>
          </div>
          <v-btn icon aria-label="Închide" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="fdlg__body">
          <div class="stickers__cols">
            <div class="stickers__options">
              <section class="form-section">
                <div class="form-section__label"><v-icon small>mdi-file-document-outline</v-icon>Hârtia</div>
                <div class="stickers__layouts" role="radiogroup" aria-label="Hârtia">
                  <button
                    v-for="l in layouts"
                    :key="l.id"
                    type="button"
                    role="radio"
                    :aria-checked="String(layout === l.id)"
                    class="stickers__layout"
                    :class="{ 'stickers__layout--on': layout === l.id }"
                    :data-layout="l.id"
                    @click="layout = l.id"
                  >
                    <v-icon class="stickers__layout-icon">{{ l.id === "labels" ? "mdi-label-multiple-outline" : "mdi-content-cut" }}</v-icon>
                    <span class="stickers__layout-text">
                      <strong>{{ l.title }}</strong>
                      <span>{{ l.subtitle }}</span>
                    </span>
                    <v-icon v-if="layout === l.id" color="#2d8659">mdi-check-circle</v-icon>
                  </button>
                </div>
              </section>

              <section v-if="layout === 'labels'" class="form-section">
                <div class="form-section__label"><v-icon small>mdi-grid</v-icon>Prima etichetă liberă pe coală</div>
                <p class="form-section__note">Pentru o coală folosită pe jumătate: atingeți prima etichetă care a rămas pe ea.</p>
                <div class="stickers__grid" role="radiogroup" aria-label="Poziția de început">
                  <button
                    v-for="n in 24"
                    :key="n"
                    type="button"
                    role="radio"
                    :aria-checked="String(start === n)"
                    :aria-label="`Începe de la eticheta ${n}`"
                    class="stickers__cell"
                    :class="{ 'stickers__cell--used': n < start, 'stickers__cell--start': n === start }"
                    :data-pos="n"
                    @click="start = n"
                  >
                    {{ n < start ? "" : n }}
                  </button>
                </div>
              </section>

              <v-alert dense text type="warning" icon="mdi-printer-alert" class="stickers__tip">
                La imprimare alegeți <strong>Scara 100%</strong> („Dimensiune reală”) și dezactivați
                <strong>„Potrivire la pagină” / „Fit to page”</strong>; margini: <strong>Implicite</strong> sau <strong>Niciuna</strong>.
                Altfel etichetele nu cad pe coală.
              </v-alert>
            </div>

            <section class="form-section stickers__preview-section">
              <div class="form-section__label">
                <v-icon small>mdi-eye-outline</v-icon>Previzualizare
                <span class="stickers__pages">{{ pagesText }}</span>
              </div>
              <div ref="frameBox" class="stickers__preview">
                <div v-if="building" class="stickers__busy">
                  <v-progress-circular indeterminate color="#2d8659" size="32" />
                </div>
                <div v-if="error" class="form-error">{{ error }}</div>
                <iframe
                  v-show="!error"
                  ref="frame"
                  class="stickers__frame"
                  title="Previzualizarea etichetelor"
                  :srcdoc="html"
                  :style="frameStyle"
                  @load="onFrameLoad"
                />
              </div>
            </section>
          </div>
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer />
          <v-btn text large @click="close">Anulare</v-btn>
          <v-btn class="btn-green" large :disabled="!ready" data-action="print" @click="print">
            <v-icon left>mdi-printer</v-icon>Imprimă
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
// Print preview + print of QR stickers. The sheet is its own document (stickerSheet.js) inside an iframe:
// printing the iframe uses its @page size and mm positions, untouched by the app's styles.
import { LAYOUTS, buildStickerDocument, perPage } from "./stickerSheet";
import { amount } from "@/helper/plural";
import { fetchStickerRows } from "@/views/equipments/bulkApi";

const LAYOUT_KEY = "suport.stickers.layout";
const MM = 96 / 25.4; // CSS px per mm

function storedLayout() {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    return LAYOUTS[v] ? v : "labels";
  } catch (e) {
    return "labels";
  }
}

export default {
  name: "StickerPrintDialog",
  props: {
    value: { type: Boolean, default: false },
    // rows with id (+ tag, type (equipmentType), model, serial (serial_number)), in the order to print; what is
    // printed is fetched again by id when the dialog opens (the page's rows may be stale, the sticker lasts years)
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      layout: storedLayout(),
      start: 1,
      html: "",
      pages: 0,
      count: 0,
      building: false,
      ready: false,
      error: "",
      scale: 0.5,
      frameHeight: 600,
      // the items as the server has them now (null until loaded), for the ids in `freshKey`
      fresh: null,
      freshKey: "",
      missing: 0,
    };
  },
  computed: {
    layouts() {
      return Object.values(LAYOUTS);
    },
    rows() {
      return this.fresh || [];
    },
    withTag() {
      return this.rows.filter((i) => i && i.tag);
    },
    countText() {
      if (!this.fresh) return this.items.length ? "Se verifică datele echipamentelor…" : amount(0, "etichetă", "etichete");
      const n = this.withTag.length;
      const untagged = this.rows.length - n;
      const notes = [];
      if (untagged > 0) notes.push(`${untagged} fără cod QR încă`);
      if (this.missing > 0) notes.push(amount(this.missing, "echipament șters între timp", "echipamente șterse între timp"));
      const base = amount(n, "etichetă", "etichete");
      return notes.length ? `${base} (${notes.join("; ")})` : base;
    },
    pagesText() {
      if (!this.pages) return "";
      const what = this.layout === "labels" ? (this.pages === 1 ? "coală" : "coli") : this.pages === 1 ? "pagină" : "pagini";
      return `${this.pages} ${what} A4`;
    },
    frameStyle() {
      // the document is drawn at 100 % (210 mm wide) and scaled down to fit the dialog
      return {
        width: `${Math.ceil(210 * MM + 24)}px`,
        height: `${this.frameHeight}px`,
        transform: `scale(${this.scale})`,
      };
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.start = 1;
        this.fresh = null; // fetched again at every opening
        this.freshKey = "";
        this.rebuild();
      }
    },
    layout(v) {
      try {
        localStorage.setItem(LAYOUT_KEY, v);
      } catch (e) {
        // not remembered
      }
      this.rebuild();
    },
    start() {
      this.rebuild();
    },
    items() {
      if (this.value) this.rebuild();
    },
  },
  mounted() {
    this.onResize = () => this.fit();
    window.addEventListener("resize", this.onResize);
    if (this.value) this.rebuild();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    async rebuild() {
      const run = (this.run = (this.run || 0) + 1);
      this.building = true;
      this.ready = false;
      this.error = "";
      try {
        try {
          await this.loadCurrent();
        } catch (e) {
          if (run !== this.run) return;
          this.error = "Datele echipamentelor nu au putut fi verificate. Verificați conexiunea și încercați din nou.";
          return;
        }
        if (run !== this.run) return;
        const doc = await buildStickerDocument(this.withTag, { layout: this.layout, start: this.start });
        if (run !== this.run) return;
        this.html = doc.html;
        this.pages = doc.pages;
        this.count = doc.count;
        // a sheet = 297 mm + its margins on screen
        this.frameHeight = Math.ceil(doc.pages * (297 * MM + 14) + 24);
        this.$nextTick(this.fit);
      } catch (e) {
        if (run !== this.run) return;
        this.error = "Codurile QR nu au putut fi generate. Verificați conexiunea și încercați din nou.";
      } finally {
        if (run === this.run) this.building = false;
      }
    },
    // the items' current tag / type / model / seria (an edit, a transfer or a delete since the page loaded)
    async loadCurrent() {
      const ids = this.items.map((i) => i && Number(i.id)).filter((id) => id > 0);
      const key = ids.join(",");
      if (this.fresh && this.freshKey === key) return;
      if (!ids.length) {
        this.fresh = this.items.filter(Boolean);
        this.missing = 0;
        this.freshKey = key;
        return;
      }
      const { rows, missing } = await fetchStickerRows(ids);
      // the items changed while it loaded: the newer call wins
      if (key !== this.items.map((i) => i && Number(i.id)).filter((id) => id > 0).join(",")) return;
      this.fresh = rows || [];
      this.missing = (missing || []).length;
      this.freshKey = key;
    },
    fit() {
      const box = this.$refs.frameBox;
      if (!box) return;
      const width = box.clientWidth || 320;
      this.scale = Math.min(1, width / (210 * MM + 24));
      box.style.height = `${Math.ceil(this.frameHeight * this.scale)}px`;
    },
    onFrameLoad() {
      this.ready = !!this.html && this.count > 0;
      this.fit();
    },
    print() {
      const frame = this.$refs.frame;
      if (!frame || !frame.contentWindow) return;
      frame.contentWindow.focus();
      frame.contentWindow.print();
    },
    perPage,
  },
};
</script>

<style scoped>
.stickers .fdlg__form {
  max-height: 92vh;
}
.stickers__cols {
  display: block;
}
@media (min-width: 900px) {
  .stickers__cols {
    display: grid;
    grid-template-columns: 360px minmax(0, 1fr);
    gap: 16px;
    align-items: start;
  }
  .stickers__preview-section {
    position: sticky;
    top: 0;
  }
}
.stickers__layouts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}
.stickers__layout {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  padding: 12px 14px;
  text-align: left;
  border: 1px solid #d6e0da;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
}
.stickers__layout--on {
  border: 2px solid #2d8659;
  background: #f2f8f4;
}
.stickers__layout-icon.v-icon {
  color: #2d8659;
}
.stickers__layout-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 2px;
  font-size: 0.92rem;
  color: #5f7068;
}
.stickers__layout-text strong {
  color: #13221c;
  font-size: 1rem;
}
.stickers__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  max-width: 250px;
  padding: 8px;
  margin-bottom: 12px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  aspect-ratio: 210 / 297;
}
.stickers__cell {
  min-height: 30px;
  border: 1px solid #c3d0c9;
  border-radius: 4px;
  background: #fff;
  font-weight: 700;
  color: #44574e;
  cursor: pointer;
}
.stickers__cell--used {
  background: repeating-linear-gradient(135deg, #eef2ef 0 6px, #e1e8e4 6px 12px);
  border-style: dashed;
}
.stickers__cell--start {
  background: #2d8659;
  border-color: #2d8659;
  color: #fff;
}
.stickers__tip {
  margin-bottom: 16px;
}
.stickers__pages {
  margin-left: auto;
  font-family: var(--font-ui);
  font-size: 0.92rem;
  font-weight: 600;
  color: #5f7068;
}
.stickers__preview {
  position: relative;
  overflow: hidden;
  margin-bottom: 10px;
  min-height: 120px;
}
.stickers__frame {
  display: block;
  border: 0;
  transform-origin: 0 0;
  background: #dfe6e2;
}
.stickers__busy {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  background: rgba(255, 255, 255, 0.6);
}
</style>
